import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Search, Filter, MapPin, List, MoreVertical, Eye, Edit, Trash2 } from 'lucide-react'
import { Button, Badge, Card } from '../../components/ui'
import type { Store } from '../../types'

// Mock data
const mockStores: Store[] = [
  {
    id: '1',
    companyId: 'comp-1',
    code: 'SHP001',
    name: '渋谷道玄坂店',
    latitude: 35.659517,
    longitude: 139.698962,
    prefecture: '東京都',
    city: '渋谷区',
    address: '道玄坂2-1-1 渋谷ビル3F',
    timezone: 'Asia/Tokyo',
    status: 'active',
    createdAt: '2024-01-15',
    updatedAt: '2025-12-01',
    groups: [
      { id: 'g1', name: '関東地区', groupType: 'region', createdAt: '' },
      { id: 'g2', name: '大型店舗', groupType: 'size', createdAt: '' },
    ],
  },
  {
    id: '2',
    companyId: 'comp-1',
    code: 'SHP002',
    name: '新宿西口店',
    latitude: 35.689506,
    longitude: 139.699640,
    prefecture: '東京都',
    city: '新宿区',
    address: '西新宿1-1-1',
    timezone: 'Asia/Tokyo',
    status: 'active',
    createdAt: '2024-02-01',
    updatedAt: '2025-12-01',
    groups: [
      { id: 'g1', name: '関東地区', groupType: 'region', createdAt: '' },
      { id: 'g2', name: '大型店舗', groupType: 'size', createdAt: '' },
    ],
  },
  {
    id: '3',
    companyId: 'comp-1',
    code: 'SHP003',
    name: '池袋東口店',
    latitude: 35.729503,
    longitude: 139.711318,
    prefecture: '東京都',
    city: '豊島区',
    address: '東池袋1-1-1',
    timezone: 'Asia/Tokyo',
    status: 'active',
    createdAt: '2024-03-01',
    updatedAt: '2025-12-01',
    groups: [
      { id: 'g1', name: '関東地区', groupType: 'region', createdAt: '' },
      { id: 'g3', name: '中型店舗', groupType: 'size', createdAt: '' },
    ],
  },
  {
    id: '4',
    companyId: 'comp-1',
    code: 'SHP004',
    name: '横浜駅前店',
    latitude: 35.466188,
    longitude: 139.622715,
    prefecture: '神奈川県',
    city: '横浜市西区',
    address: '南幸1-1-1',
    timezone: 'Asia/Tokyo',
    status: 'inactive',
    createdAt: '2024-04-01',
    updatedAt: '2025-11-15',
    groups: [
      { id: 'g1', name: '関東地区', groupType: 'region', createdAt: '' },
      { id: 'g3', name: '中型店舗', groupType: 'size', createdAt: '' },
    ],
  },
  {
    id: '5',
    companyId: 'comp-1',
    code: 'SHP005',
    name: '大阪梅田店',
    latitude: 34.702485,
    longitude: 135.495951,
    prefecture: '大阪府',
    city: '大阪市北区',
    address: '梅田1-1-1',
    timezone: 'Asia/Tokyo',
    status: 'active',
    createdAt: '2024-05-01',
    updatedAt: '2025-12-01',
    groups: [
      { id: 'g4', name: '関西地区', groupType: 'region', createdAt: '' },
      { id: 'g2', name: '大型店舗', groupType: 'size', createdAt: '' },
    ],
  },
]

const statusConfig = {
  active: { label: '稼働中', variant: 'success' as const },
  inactive: { label: '停止中', variant: 'danger' as const },
  maintenance: { label: 'メンテナンス', variant: 'warning' as const },
}

export default function StoreListPage() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [prefectureFilter, setPrefectureFilter] = useState('all')
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list')
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const filteredStores = mockStores.filter(store => {
    const matchesSearch = 
      store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      store.code.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesPrefecture = prefectureFilter === 'all' || store.prefecture === prefectureFilter
    return matchesSearch && matchesPrefecture
  })

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredStores.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(filteredStores.map(s => s.id))
    }
  }

  const prefectures = [...new Set(mockStores.map(s => s.prefecture).filter(Boolean))]

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">店舗管理</h1>
          <p className="text-sm text-gray-500 mt-1">店舗の登録・管理</p>
        </div>
        <Link to="/stores/new">
          <Button leftIcon={<Plus className="w-4 h-4" />}>
            新規店舗登録
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="店舗名・店舗コードで検索..."
              className="form-input pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <select 
              className="form-select w-40"
              value={prefectureFilter}
              onChange={(e) => setPrefectureFilter(e.target.value)}
            >
              <option value="all">すべての地域</option>
              {prefectures.map(pref => (
                <option key={pref} value={pref}>{pref}</option>
              ))}
            </select>
            <div className="flex border border-gray-300 rounded-md">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
              >
                <List className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`p-2 ${viewMode === 'map' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
              >
                <MapPin className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <Button variant="secondary" leftIcon={<Filter className="w-4 h-4" />}>
              詳細フィルタ
            </Button>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th className="w-10">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    checked={selectedIds.length === filteredStores.length && filteredStores.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>店舗コード</th>
                <th>店舗名</th>
                <th>住所</th>
                <th>グループ</th>
                <th>ステータス</th>
                <th className="w-20">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredStores.map((store) => {
                const status = statusConfig[store.status]
                
                return (
                  <tr 
                    key={store.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/stores/${store.id}`)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="form-checkbox"
                        checked={selectedIds.includes(store.id)}
                        onChange={() => toggleSelect(store.id)}
                      />
                    </td>
                    <td className="font-mono text-sm">{store.code}</td>
                    <td>
                      <div className="font-medium text-gray-900">{store.name}</div>
                    </td>
                    <td className="text-gray-600">
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 text-gray-400 mr-1" />
                        <span className="text-sm">
                          {store.prefecture}{store.city}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {store.groups?.slice(0, 2).map(group => (
                          <Badge key={group.id} variant="gray">
                            {group.name}
                          </Badge>
                        ))}
                        {(store.groups?.length || 0) > 2 && (
                          <Badge variant="gray">+{(store.groups?.length || 0) - 2}</Badge>
                        )}
                      </div>
                    </td>
                    <td>
                      <Badge variant={status.variant} dot>
                        {status.label}
                      </Badge>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="relative group">
                        <button className="p-2 hover:bg-gray-100 rounded-lg">
                          <MoreVertical className="w-4 h-4 text-gray-500" />
                        </button>
                        <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                          <button 
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                            onClick={() => navigate(`/stores/${store.id}`)}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            詳細
                          </button>
                          <button 
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                            onClick={() => navigate(`/stores/${store.id}/edit`)}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            編集
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center">
                            <Trash2 className="w-4 h-4 mr-2" />
                            削除
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            全{filteredStores.length}件中 1〜{filteredStores.length}件を表示
          </div>
          <div className="flex items-center space-x-2">
            <button className="pagination-btn rounded-l-md" disabled>
              前へ
            </button>
            <button className="pagination-btn pagination-btn-active">1</button>
            <button className="pagination-btn rounded-r-md" disabled>
              次へ
            </button>
          </div>
        </div>
      </Card>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-4 animate-fadeIn">
          <span className="text-sm">{selectedIds.length}件選択中</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" className="text-white hover:bg-gray-800">
              グループに追加
            </Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-gray-800">
              一括編集
            </Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-gray-800">
              エクスポート
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

